/**
 * @fileoverview JWT-backed auth instance for the app session. Lives in the api
 * layer because the api client needs the session token to authenticate requests;
 * features and the app shell consume it via `@/api`. Stores the session JWT in
 * localStorage, exposes the currentUser / onAuthStateChanged API, and clears the
 * session when the token has expired or the API rejects it.
 */

const STORAGE_KEY = 'toolpad_session';
const AUTH_CHANGED_EVENT = 'toolpad_auth_changed';

interface StoredSession {
  token: string;
  user: {
    uid: string;
    name: string;
    email: string;
    picture: string;
    github_login: string;
  };
}

export interface CurrentUser {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
  providerData: { providerId: string; uid: string }[];
  getIdToken(forceRefresh?: boolean): Promise<string>;
}

function getStoredSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function sessionToUser(session: StoredSession): CurrentUser {
  return {
    uid: session.user.uid,
    displayName: session.user.name || null,
    email: session.user.email || null,
    photoURL: session.user.picture || null,
    providerData: [{ providerId: 'github.com', uid: session.user.uid }],
    getIdToken: (_forceRefresh?: boolean) => Promise.resolve(session.token),
  };
}

export function storeSession(session: StoredSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

/**
 * Reads the `exp` claim (seconds since the epoch) from a JWT without verifying
 * it. Returns null when the token has no readable `exp`; the backend remains the
 * authority on whether a token is valid.
 */
function getTokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
}

/**
 * Clears the stored session when its JWT has already expired, so the app starts
 * signed out and goes straight to the sign-in page instead of rendering pages
 * whose requests would all be rejected. Returns true when a session was cleared.
 */
export function clearExpiredSession(now: number = Date.now()): boolean {
  const session = getStoredSession();
  if (!session) return false;

  const expiry = getTokenExpiry(session.token);
  if (expiry === null || expiry * 1000 > now) return false;

  clearSession();
  return true;
}

/**
 * Signs the user out when the API answers 401 (missing, invalid or expired
 * token). Clearing the session notifies auth listeners, and the app shell then
 * redirects to the sign-in page. Every request that sends the session token
 * should pass its response here, including ones that bypass `apiClient`.
 */
export function handleUnauthorizedResponse(response: Pick<Response, 'status'>): void {
  if (response.status === 401) {
    clearSession();
  }
}

export const sessionAuth = {
  get currentUser(): CurrentUser | null {
    const session = getStoredSession();
    return session ? sessionToUser(session) : null;
  },

  onAuthStateChanged(callback: (user: CurrentUser | null) => void): () => void {
    let cancelled = false;

    // Defer first call so callers can capture the unsubscribe function first,
    // then fire a second time to signal that auth initialisation is complete
    // (two-callback pattern so callers can distinguish "still loading" from "no user").
    queueMicrotask(() => {
      if (cancelled) return;
      callback(this.currentUser);
      queueMicrotask(() => {
        if (!cancelled) callback(this.currentUser);
      });
    });

    const handler = () => {
      if (!cancelled) callback(this.currentUser);
    };
    window.addEventListener(AUTH_CHANGED_EVENT, handler);

    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_CHANGED_EVENT, handler);
    };
  },
};
